import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogBuyersPremium } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getUrlFromPathname } from '@/redux/modules/config';
import { getCurrencyConversion } from '@/redux/modules/currency';
import {
    getItemSummaryBuyNowPrice,
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { TrackBuyNowPressedAnalyticsPayload } from '@/utils/amplitude/analytics/bidding/bidding.types';
import { track } from '@/utils/amplitude/analytics/core';
import { TrackingEvents } from '@/utils/amplitude/analytics/core.types';
import { calculateBuyersPremium } from '@/utils/calculateBuyersPremium';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

const trackBuyNowPressedAnalytics = (payload: TrackBuyNowPressedAnalyticsPayload) => {
    track(TrackingEvents.Bidding.BuyNowPressed, payload);
};

export const useTrackBuyNowPressedAnalytics = (itemId: number) => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));

    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const buyNowPrice = useAppSelector((state) => getItemSummaryBuyNowPrice(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const currencyConversion = useAppSelector((state) => getCurrencyConversion(state, currency));
    const buyersPremium = useAppSelector((state) => getCatalogBuyersPremium(state, catalogId));

    return useCallback(
        (makeAnOfferButton: boolean) => {
            const conversionToUsd = currencyConversion?.conversionToUsd ?? 0;
            const { amount: buyersPremiumAmount } = calculateBuyersPremium(buyNowPrice, buyersPremium);
            const lotHammerPriceUsd = buyNowPrice * conversionToUsd;
            const totalAmount = buyNowPrice + buyersPremiumAmount;
            const lotTotalPriceUsd = totalAmount * conversionToUsd;

            trackBuyNowPressedAnalytics({
                lotHammerPriceUsd,
                lotTotalPriceUsd,
                makeAnOfferButton,
                pageName: getAmplitudePageNameFromPath(pathname),
                url,
            });
        },
        [buyNowPrice, buyersPremium, currencyConversion?.conversionToUsd, pathname, url]
    );
};
